import type { ThemeDefinition } from "../theme";

/** Built-in Dark theme — colors only; structure comes from `baseTheme.ts`. */
export const darkTheme: ThemeDefinition = {
  id: "dark",
  name: "Dark",
  builtIn: true,
  palette: {
    mode: "dark",
    primary: { main: "#00B386" },
    secondary: { main: "#39C79A" },
    error: { main: "#B3261E" },
    background: { default: "#0A1015", paper: "#121A20" },
  },
  appColors: {
    headerLogoColor: "#E6EDF5",
    headerBackground: "#111922",
    headerNavLinks: { textColor: "#A2B2C6" },
    headerSearch: {
      placeholderColor: "#9CA3AF",
      iconAndTextColor: "#9CA3AF",
      borderColor: "#475569",
      backgroundColor: "#1E293B",
      restingPlaceholderColor: "#6B7280",
      filledTextColor: "#E2E8F0",
      activeColor: "#4C97F0",
      activeRowBackground: "rgba(76, 151, 240, 0.16)",
      cardBackground: "#121A20",
      cardHeaderBackground: "#1A2430",
      cardPlaceholderColor: "#6B7486",
      rowBorderColor: "#1E2A36",
      cusipColor: "#4C97F0",
      nameColor: "#E4E8EF",
      typeColor: "#9AA3B2",
      couponColor: "#E4E8EF",
      yearColor: "#6B7486",
      footerColor: "#4F5869",
      closeColor: "#6B7486",
    },
    dataGrid: {
      secondaryTextColor: "#8999AB",
      headerTextColor: "#8999AB",
      headerBackgroundColor: "#1A2430",
      headerSurfaceColor: "#1A2430",
      headerRuleColor: "rgba(255, 255, 255, 0.08)",
      rowBorderColor: "#1E2A36",
      footerBackgroundColor: "#1A2430",
      footerBorderColor: "rgba(255, 255, 255, 0.08)",
      selectionCheckboxColor: "#767676",
      headerSortedTextColor: "#CBD5E1",
      cellTextColor: "#E6EDF5",
      cusipLinkColor: "#60A5FA",
      activeRowBorderColor: "#00B386",
      activeRowBackgroundColor: "rgba(0, 179, 134, 0.16)",
      watchlistMetrics: {
        offers: "rgba(59, 130, 246, 0.16)",
        bids: "rgba(255, 167, 38, 0.16)",
        rfqs: "rgba(0, 179, 134, 0.14)",
        trades: "rgba(173, 70, 255, 0.16)",
      },
      watchlistMetricHeaders: {
        offers: "#1C365E",
        bids: "#664C25",
        rfqs: "#105850",
        trades: "#573184",
      },
      watchlistMetricInk: {
        offers: { count: "#60A5FA", quantity: "#3B82F6" },
        bids: { count: "#FDBA74", quantity: "#FDBA74" },
        rfqs: { count: "#4ADE80", quantity: "#86EFAC" },
        trades: { count: "#D8B4FE", quantity: "#E9D5FF" },
      },
      bondTypeChip: {
        go: { bg: "rgba(59, 130, 246, 0.18)", fg: "#93C5FD" },
        revenue: { bg: "rgba(16, 185, 129, 0.18)", fg: "#6EE7B7" },
        unclassified: "#8999AB",
        feature: { border: "#334155", fg: "#A2B2C6" },
      },
    },
    filterBar: {
      endpointSwitcherBackground: "#1A2430",
      tabSwitcherBackground: "#1A2430",
      tabSwitcherBorderColor: "rgba(255, 255, 255, 0.23)",
      tabSwitcherTextColor: "#F8FAFC",
      tabSwitcherIconColor: "rgba(255, 255, 255, 0.54)",
      barLabelColor: "#94A3B8",
      filterTriggerTextColor: "#60A5FA",
      filterTriggerBorderColor: "rgba(96, 165, 250, 0.5)",
      advancedButtonTextColor: "#FFFFFF",
      filledActionBackgroundColor: "#1D4ED8",
      clearButtonHoverBackground: "rgba(255, 255, 255, 0.08)",
    },
    filter: {
      labelColor: "#8999AB",
      inputTextColor: "#A0AFBF",
      inputPlaceholderColor: "#6B7A8D",
      inputBackgroundColor: "#1A2430",
      wrapperBorderColor: "#2A3A4A",
      wrapperBackgroundColor: "#121A20",
      identifierInput: {
        backgroundColor: "#1A2430",
        textColor: "#A0AFBF",
        placeholderColor: "rgba(230, 237, 245, 0.5)",
        borderColor: "rgba(255, 255, 255, 0.08)",
        labelColor: "#94A3B8",
        suggestionTextColor: "#E6EDF5",
        chip: {
          validText: "#93C5FD",
          validBackground: "rgba(59, 130, 246, 0.18)",
          invalidText: "#FCA5A5",
          invalidBackground: "rgba(193, 0, 7, 0.22)",
        },
      },
      numericInput: {
        labelColor: "#94A3B8",
        backgroundColor: "#1A2430",
        borderColor: "rgba(255, 255, 255, 0.23)",
        textColor: "#E6EDF5",
        placeholderColor: "rgba(230, 237, 245, 0.5)",
        checkboxColor: "#94A3B8",
        selectTextColor: "#E6EDF5",
        selectIconColor: "rgba(255, 255, 255, 0.54)",
      },
      toggleTab: {
        activeColor: "#60A5FA",
        inactiveColor: "#94A3B8",
        dividerColor: "rgba(255, 255, 255, 0.08)",
      },
    },
    dialog: {
      borderColor: "#314158",
      footerBackground: "#1A2430",
      footerBorderColor: "rgba(255, 255, 255, 0.08)",
      footerOutlinedHoverBackground: "rgba(255, 255, 255, 0.08)",
      footerPrimaryButtonShadow: "0 1px 2px rgba(0, 0, 0, 0.35), 0 1px 3px rgba(0, 0, 0, 0.28)",
      footerPrimaryButtonBackground: "#1D4ED8",
      navBackground: "#1A2430",
      navSectionLabelColor: "#94A3B8",
      navItemColor: "#94A3B8",
      navItemHoverBackground: "rgba(255, 255, 255, 0.08)",
      navItemActiveColor: "#93C5FD",
      navItemActiveHoverBackground: "rgba(25, 118, 210, 0.16)",
      // Dialog headers stay dark in both themes.
      headerBackground: "#212121",
      headerTextColor: "#FFFFFF",
      headerIconColor: "#D1D5DB",
      headerCloseIconColor: "#E0E0E0",
      headerCloseHoverBackground: "#424242",
      headerSubtitleColor: "rgba(255,255,255,0.7)",
      sectionTitleColor: "#E6EDF5",
      sectionAccentColor: "#93C5FD",
      listRowLabelColor: "#94A3B8",
      listToggleTextColor: "#94A3B8",
      listToggleBorderColor: "rgba(255, 255, 255, 0.08)",
      listToggleHoverBackground: "rgba(255, 255, 255, 0.13)",
      listToggleSuccessBackground: "#16A34A",
      listToggleErrorBackground: "#DC2626",
      listToggleErrorShadow: "0 4px 12px rgba(0, 0, 0, 0.5), 0 1px 3px rgba(0, 0, 0, 0.35)",
      listToggleSelectedTextColor: "#FFFFFF",
      accentTextColor: "#93C5FD",
      deleteIconColor: "#F87171",
      destructiveButtonTextColor: "#FFFFFF",
    },
    accountMenu: {
      background: "#1A2430",
      headerBackground: "#111922",
      headerHoverBackground: "rgba(255, 255, 255, 0.08)",
      dividerColor: "rgba(255, 255, 255, 0.08)",
      primaryTextColor: "#E6EDF5",
      secondaryTextColor: "#94A3B8",
      iconColor: "rgba(255, 255, 255, 0.7)",
      demoIconColor: "#A78BFA",
      switchTrackColor: "#475569",
      logoutColor: "#F87171",
    },
    makeCorrectionsDialog: {
      optionalLabelColor: "#64748B",
      inputTextColor: "#E6EDF5",
      unchangedAccentColor: "#66bb6a",
      changedAccentColor: "#ffa726",
      editIconColor: "#4C8DFF",
    },
    messageMetrics: {
      // Cards are delineated by a border on the dark surface, not a shadow.
      cardShadow: "none",
      cardBorder: "1px solid",
      // Tints need more of the tone to register against the dark surface.
      toneFillOpacity: 0.14,
      // Matches the dark `background.default`, so the dark theme is unchanged.
      pageBackground: "#0A1015",
      // Preserves the dark MUI `text.secondary` these headers used before.
      sourcesHeaderText: "rgba(255, 255, 255, 0.7)",
      periodToggle: {
        borderOpacity: 0.24,
        selectedOpacity: 0.16,
        selectedHoverOpacity: 0.22,
        hoverOpacity: 0.04,
      },
      // Brighter than their light counterparts so the series read against the
      // dark `background.paper` rather than sinking into it.
      chart: {
        dailyBar: "#60A5FA",
        cumulativeLine: "#818CF8",
        limitLine: "#F87171",
        axisLine: "#1E2A36",
      },
    },
    aiAssist: {
      pageBg: "#121A20",
      sidebarBg: "#0E141A",
      cardBg: "#1A2430",
      raisedBg: "#1A2430",
      headerBg: "#1A2430",
      inputBg: "#1A2430",
      avatarBg: "#334155",
      border: "#1E2A36",
      borderSubtle: "rgba(255, 255, 255, 0.08)",
      textTitle: "#E6EDF5",
      textBody: "#E6EDF5",
      textSecondary: "#8999AB",
      textMuted: "#8999AB",
      textFaint: "#6B7A8D",
      textHint: "#64748B",
      textStrong: "#CBD5E1",
      textConversation: "#CBD5E1",
      onAccent: "#FFFFFF",
      accent: "#60A5FA",
      accentWash: "rgba(96, 165, 250, 0.16)",
      accentStrong: "#3B82F6",
      userBubbleBg: "#1D4ED8",
      kindBlue: "#60A5FA",
      kindGreen: "#34D399",
      kindAmber: "#F59E0B",
      kindPurple: "#A78BFA",
      kindRed: "#EF4444",
      gradientStart: "#2563EB",
      gradientEnd: "#7C3AED",
      meterGradientStart: "#3B82F6",
      meterGradientEnd: "#8B5CF6",
      fabBadgeBg: "#EF4444",
      fabShadow: "0px 8px 32px rgba(96, 165, 250, 0.5)",
      fabShadowHover: "0px 8px 32px rgba(96, 165, 250, 0.6)",
      fabBadgeShadow: "0px 1px 4px rgba(0, 0, 0, 0.5)",
      buttonShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
      buttonShadowHover: "0 6px 16px rgba(59, 130, 246, 0.5)",
      selectedBg: "rgba(96, 165, 250, 0.16)",
      selectedBgHover: "rgba(96, 165, 250, 0.24)",
      composerShadow: "0 8px 28px rgba(0, 0, 0, 0.5)",
      composerActiveShadow: "0 8px 28px rgba(96, 165, 250, 0.35)",
      bubbleShadow: "0px 1px 6px rgba(0, 0, 0, 0.4)",
      chart: {
        axis: "#8999AB",
        grid: "#1E2A36",
        title: "#8999AB",
        markFill: "#121A20",
        preBg: "#1A2430",
        series: ["#60A5FA", "#34D399", "#9AA3AF", "#93C5FD", "#64748B"],
      },
      bondCard: {
        border: "#1E2A36",
        rowBorder: "#1E2A36",
        statsBg: "#1A2430",
        headerBg: "#0C1A2B",
        headerText: "rgba(255, 255, 255, 0.85)",
        cusip: "#5AA2FF",
        value: "#E6EDF5",
        label: "#8999AB",
        positive: "#34D399",
        qtyValue: "#E6EDF5",
        ratingBg: "rgba(52, 211, 153, 0.16)",
        ratingText: "#86EFAC",
        stateBg: "rgba(100, 116, 139, 0.2)",
        stateText: "#8999AB",
        taxBg: "rgba(34, 197, 94, 0.16)",
        taxText: "#4ADE80",
        buyBg: "rgba(15, 157, 99, 0.2)",
        buyBorder: "rgba(15, 157, 99, 0.4)",
      },
    },
  },
};
